import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { collection, getDocs, doc, updateDoc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { getUserData } from '../../../services/auth';
import { CheckCircle, XCircle, MapPin, Globe, Instagram, Clock, ChevronDown, ChevronUp, Mail, User, FileText } from 'lucide-react';

const Approvals = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [code, setCode] = useState('');
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(null);
  const [error, setError] = useState('');
  const [coHostRequests, setCoHostRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('hosts'); // 'hosts' or 'cohosts'
  const [expandedAppId, setExpandedAppId] = useState(null);

  const SECRET_CODE = '7777';
  const ALLOWED_EMAILS = ['davies@kantin.io', 'daviesodu@gmail.com'];
  const enteredCode = searchParams.get('code');
  const isAllowedEmail = user?.email && ALLOWED_EMAILS.includes(user.email);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Allow access if code matches OR if user has allowed email
    if (enteredCode === SECRET_CODE || isAllowedEmail) {
      loadApplications();
      loadCoHostRequests();
    }
  }, [user, enteredCode, isAllowedEmail, navigate]);

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    if (code === SECRET_CODE) {
      setSearchParams({ code: SECRET_CODE });
      loadApplications();
      loadCoHostRequests();
    } else {
      setError('Invalid code');
    }
  };

  const loadApplications = async () => {
    setLoading(true);
    try {
      const applicationsRef = collection(db, 'hostApplications');
      const q = query(applicationsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const appsData = await Promise.all(
        snapshot.docs.map(async (docSnapshot) => {
          const data = docSnapshot.data();
          // Get user data for each application
          let userData = null;
          try {
            userData = await getUserData(data.userId);
          } catch (err) {
            console.error('Error loading user data:', err);
          }
          
          return {
            id: docSnapshot.id,
            ...data,
            user: userData,
          };
        })
      );
      
      setApplications(appsData);
    } catch (err) {
      console.error('Error loading applications:', err);
      setError('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const loadCoHostRequests = async () => {
    try {
      const requestsRef = collection(db, 'coHostRequests');
      const q = query(requestsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const requestsData = snapshot.docs.map(docSnapshot => ({
        id: docSnapshot.id,
        ...docSnapshot.data(),
      }));
      
      setCoHostRequests(requestsData);
    } catch (err) {
      console.error('Error loading co-host requests:', err);
    }
  };

  const handleApprove = async (applicationId, userId) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Are you sure you want to approve this host application?')) {
      return;
    }

    setApproving(applicationId);
    try {
      // Update user document to set approvedAsHost to true
      await updateDoc(doc(db, 'users', userId), {
        approvedAsHost: true,
        updatedAt: serverTimestamp(),
      });

      // Update application status
      await updateDoc(doc(db, 'hostApplications', applicationId), {
        status: 'approved',
        approvedAt: serverTimestamp(),
        approvedBy: user.uid,
        updatedAt: serverTimestamp(),
      });

      // Reload applications
      await loadApplications();
    } catch (err) {
      console.error('Error approving application:', err);
      setError('Failed to approve application');
    } finally {
      setApproving(null);
    }
  };

  const handleReject = async (applicationId) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Are you sure you want to reject this host application?')) {
      return;
    }

    setApproving(applicationId);
    try {
      // Update application status
      await updateDoc(doc(db, 'hostApplications', applicationId), {
        status: 'rejected',
        rejectedAt: serverTimestamp(),
        rejectedBy: user.uid,
        updatedAt: serverTimestamp(),
      });

      // Reload applications
      await loadApplications();
    } catch (err) {
      console.error('Error rejecting application:', err);
      setError('Failed to reject application');
    } finally {
      setApproving(null);
    }
  };

  // Code entry screen (skip if user has allowed email)
  if (!isAllowedEmail && (!enteredCode || enteredCode !== SECRET_CODE)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50/20 via-white to-green-50/10">
        <div className="max-w-md w-full bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-8">
          <h1 className="text-2xl font-semibold text-gray-900 mb-4">Enter Access Code</h1>
          <form onSubmit={handleCodeSubmit} className="space-y-4">
            {error && (
              <div className="rounded-lg bg-red-50 border-2 border-red-300 p-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
                Access Code
              </label>
              <input
                id="code"
                type="password"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                  setError('');
                }}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter code"
                autoFocus
              />
            </div>
            <button
              type="submit"
              className="w-full bg-emerald-700 hover:bg-emerald-800 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              Access Approvals
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Approvals list screen
  const pendingApplications = applications.filter(app => app.status === 'pending');
  const approvedApplications = applications.filter(app => app.status === 'approved');
  const rejectedApplications = applications.filter(app => app.status === 'rejected');

  // Co-host requests
  const pendingCoHostRequests = coHostRequests.filter(req => req.status === 'pending');
  const approvedCoHostRequests = coHostRequests.filter(req => req.status === 'approved');
  const rejectedCoHostRequests = coHostRequests.filter(req => req.status === 'rejected');

  const handleApproveCoHost = async (requestId) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Are you sure you want to approve this co-host request?')) {
      return;
    }

    setApproving(requestId);
    try {
      await updateDoc(doc(db, 'coHostRequests', requestId), {
        status: 'approved',
        approvedAt: serverTimestamp(),
        approvedBy: user.uid,
        updatedAt: serverTimestamp(),
      });

      await loadCoHostRequests();
    } catch (err) {
      console.error('Error approving co-host request:', err);
      setError('Failed to approve co-host request');
    } finally {
      setApproving(null);
    }
  };

  const handleRejectCoHost = async (requestId) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Are you sure you want to reject this co-host request?')) {
      return;
    }

    setApproving(requestId);
    try {
      await updateDoc(doc(db, 'coHostRequests', requestId), {
        status: 'rejected',
        rejectedAt: serverTimestamp(),
        rejectedBy: user.uid,
        updatedAt: serverTimestamp(),
      });

      await loadCoHostRequests();
    } catch (err) {
      console.error('Error rejecting co-host request:', err);
      setError('Failed to reject co-host request');
    } finally {
      setApproving(null);
    }
  };

  // Component to render co-host request details
  const renderCoHostRequestDetails = (request) => {
    const createdAt = request.createdAt?.toDate 
      ? request.createdAt.toDate() 
      : request.createdAt 
      ? new Date(request.createdAt) 
      : null;
    
    const approvedAt = request.approvedAt?.toDate 
      ? request.approvedAt.toDate() 
      : request.approvedAt 
      ? new Date(request.approvedAt) 
      : null;
    
    const rejectedAt = request.rejectedAt?.toDate 
      ? request.rejectedAt.toDate() 
      : request.rejectedAt 
      ? new Date(request.rejectedAt) 
      : null;

    return (
      <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {request.email && (
            <div className="flex items-start gap-2">
              <Mail className="w-4 h-4 text-emerald-600 mt-1 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Email</p>
                <a
                  href={`mailto:${request.email}`}
                  className="text-sm font-medium text-emerald-700 hover:text-emerald-800"
                >
                  {request.email}
                </a>
              </div>
            </div>
          )}
          {request.location && (
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-emerald-600 mt-1 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Location</p>
                <p className="text-sm font-medium text-gray-900">{request.location}</p>
              </div>
            </div>
          )}
          {request.website && (
            <div className="flex items-start gap-2">
              <Globe className="w-4 h-4 text-emerald-600 mt-1 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Website</p>
                <a
                  href={request.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-emerald-700 hover:text-emerald-800"
                >
                  {request.website}
                </a>
              </div>
            </div>
          )}
          {request.instagram && (
            <div className="flex items-start gap-2">
              <Instagram className="w-4 h-4 text-emerald-600 mt-1 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Instagram</p>
                <p className="text-sm font-medium text-gray-900">{request.instagram}</p>
              </div>
            </div>
          )}
          {request.socialMedia && (
            <div className="flex items-start gap-2">
              <Globe className="w-4 h-4 text-emerald-600 mt-1 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Social Media</p>
                <p className="text-sm font-medium text-gray-900">{request.socialMedia}</p>
              </div>
            </div>
          )}
        </div>

        {request.experience && (
          <div className="pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 mb-1">Hosting Experience</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{request.experience}</p>
          </div>
        )}

        {request.bio && (
          <div className="pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 mb-1">About</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{request.bio}</p>
          </div>
        )}

        <div className="pt-4 border-t border-gray-200 space-y-2">
          {createdAt && (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Clock className="w-3 h-3" />
              <span>Requested {createdAt.toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
              })}</span>
            </div>
          )}
          {approvedAt && (
            <div className="flex items-center gap-2 text-xs text-green-600">
              <CheckCircle className="w-3 h-3" />
              <span>Approved {approvedAt.toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
              })}</span>
            </div>
          )}
          {rejectedAt && (
            <div className="flex items-center gap-2 text-xs text-red-600">
              <XCircle className="w-3 h-3" />
              <span>Rejected {rejectedAt.toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
              })}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Component to render application details
  const renderApplicationDetails = (app) => {
    const createdAt = app.createdAt?.toDate 
      ? app.createdAt.toDate() 
      : app.createdAt 
      ? new Date(app.createdAt) 
      : null;
    
    const approvedAt = app.approvedAt?.toDate 
      ? app.approvedAt.toDate() 
      : app.approvedAt 
      ? new Date(app.approvedAt) 
      : null;
    
    const rejectedAt = app.rejectedAt?.toDate 
      ? app.rejectedAt.toDate() 
      : app.rejectedAt 
      ? new Date(app.rejectedAt) 
      : null;

    return (
      <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {app.location && (
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-emerald-600 mt-1 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Location</p>
                <p className="text-sm font-medium text-gray-900">{app.location}</p>
              </div>
            </div>
          )}
          {app.website && (
            <div className="flex items-start gap-2">
              <Globe className="w-4 h-4 text-emerald-600 mt-1 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Website</p>
                <a
                  href={app.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-emerald-700 hover:text-emerald-800"
                >
                  {app.website}
                </a>
              </div>
            </div>
          )}
          {app.instagram && (
            <div className="flex items-start gap-2">
              <Instagram className="w-4 h-4 text-emerald-600 mt-1 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Instagram</p>
                <p className="text-sm font-medium text-gray-900">{app.instagram}</p>
              </div>
            </div>
          )}
          {app.socialMedia && (
            <div className="flex items-start gap-2">
              <Globe className="w-4 h-4 text-emerald-600 mt-1 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Social Media</p>
                <p className="text-sm font-medium text-gray-900">{app.socialMedia}</p>
              </div>
            </div>
          )}
        </div>

        {app.bio && (
          <div className="pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 mb-1">About their space</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{app.bio}</p>
          </div>
        )}

        <div className="pt-4 border-t border-gray-200 space-y-2">
          {createdAt && (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Clock className="w-3 h-3" />
              <span>Applied {createdAt.toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
              })}</span>
            </div>
          )}
          {approvedAt && (
            <div className="flex items-center gap-2 text-xs text-green-600">
              <CheckCircle className="w-3 h-3" />
              <span>Approved {approvedAt.toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
              })}</span>
            </div>
          )}
          {rejectedAt && (
            <div className="flex items-center gap-2 text-xs text-red-600">
              <XCircle className="w-3 h-3" />
              <span>Rejected {rejectedAt.toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
              })}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50/20 via-white to-green-50/10 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Approvals</h1>
          <p className="text-lg text-gray-600">Review and approve host applications and co-host requests</p>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('hosts')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'hosts'
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Host Applications ({applications.length})
            </button>
            <button
              onClick={() => setActiveTab('cohosts')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'cohosts'
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Co-host Requests ({coHostRequests.length})
            </button>
          </nav>
        </div>

        {error && (
          <div className="mb-6 rounded-lg bg-red-50 border-2 border-red-300 p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-emerald-600 border-t-transparent"></div>
          </div>
        ) : (
          <div className="space-y-8">
            {activeTab === 'hosts' ? (
              <>
            {/* Pending Applications */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Pending ({pendingApplications.length})
              </h2>
              {pendingApplications.length === 0 ? (
                <div className="bg-white rounded-lg shadow-md p-8 text-center">
                  <p className="text-gray-500">No pending applications</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingApplications.map((app) => {
                    const createdAt = app.createdAt?.toDate 
                      ? app.createdAt.toDate() 
                      : app.createdAt 
                      ? new Date(app.createdAt) 
                      : null;

                    return (
                      <div key={app.id} className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              {app.user?.photoURL ? (
                                <img
                                  src={app.user.photoURL}
                                  alt={app.user.displayName || 'User'}
                                  className="w-12 h-12 rounded-full"
                                />
                              ) : (
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-600 to-green-700 flex items-center justify-center text-white font-medium">
                                  {(app.user?.displayName || app.user?.email || 'U')[0].toUpperCase()}
                                </div>
                              )}
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                  {app.user?.displayName || app.user?.email || 'Unknown User'}
                                </h3>
                                {app.user?.email && (
                                  <p className="text-sm text-gray-600">{app.user.email}</p>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleApprove(app.id, app.userId)}
                              disabled={approving === app.id}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <CheckCircle className="w-4 h-4" />
                              Approve
                            </button>
                            <button
                              onClick={() => handleReject(app.id)}
                              disabled={approving === app.id}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-red-700 hover:bg-red-800 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <XCircle className="w-4 h-4" />
                              Reject
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                          {app.location && (
                            <div className="flex items-start gap-2">
                              <MapPin className="w-4 h-4 text-emerald-600 mt-1 flex-shrink-0" />
                              <div>
                                <p className="text-xs text-gray-500">Location</p>
                                <p className="text-sm font-medium text-gray-900">{app.location}</p>
                              </div>
                            </div>
                          )}
                          {app.website && (
                            <div className="flex items-start gap-2">
                              <Globe className="w-4 h-4 text-emerald-600 mt-1 flex-shrink-0" />
                              <div>
                                <p className="text-xs text-gray-500">Website</p>
                                <a
                                  href={app.website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm font-medium text-emerald-700 hover:text-emerald-800"
                                >
                                  {app.website}
                                </a>
                              </div>
                            </div>
                          )}
                          {app.instagram && (
                            <div className="flex items-start gap-2">
                              <Instagram className="w-4 h-4 text-emerald-600 mt-1 flex-shrink-0" />
                              <div>
                                <p className="text-xs text-gray-500">Instagram</p>
                                <p className="text-sm font-medium text-gray-900">{app.instagram}</p>
                              </div>
                            </div>
                          )}
                          {app.socialMedia && (
                            <div className="flex items-start gap-2">
                              <Globe className="w-4 h-4 text-emerald-600 mt-1 flex-shrink-0" />
                              <div>
                                <p className="text-xs text-gray-500">Social Media</p>
                                <p className="text-sm font-medium text-gray-900">{app.socialMedia}</p>
                              </div>
                            </div>
                          )}
                        </div>

                        {app.bio && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <p className="text-xs text-gray-500 mb-1">About their space</p>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{app.bio}</p>
                          </div>
                        )}

                        {createdAt && (
                          <div className="mt-4 pt-4 border-t border-gray-200 flex items-center gap-2 text-xs text-gray-500">
                            <Clock className="w-3 h-3" />
                            <span>Applied {createdAt.toLocaleDateString('en-US', { 
                              month: 'long', 
                              day: 'numeric', 
                              year: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit'
                            })}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Approved Applications */}
            {approvedApplications.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Approved ({approvedApplications.length})
                </h2>
                <div className="space-y-4">
                  {approvedApplications.map((app) => {
                    const isExpanded = expandedAppId === app.id;
                    return (
                      <div key={app.id} className="bg-green-50 rounded-lg shadow-md border-2 border-green-200 overflow-hidden">
                        <button
                          onClick={() => setExpandedAppId(isExpanded ? null : app.id)}
                          className="w-full p-6 flex items-center justify-between hover:bg-green-100/50 transition-colors text-left"
                        >
                          <div className="flex items-center gap-3 flex-1">
                            {app.user?.photoURL ? (
                              <img
                                src={app.user.photoURL}
                                alt={app.user.displayName || 'User'}
                                className="w-12 h-12 rounded-full"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-600 to-green-700 flex items-center justify-center text-white font-medium">
                                {(app.user?.displayName || app.user?.email || 'U')[0].toUpperCase()}
                              </div>
                            )}
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-gray-900">
                                {app.user?.displayName || app.user?.email || 'Unknown User'}
                              </h3>
                              {app.user?.email && (
                                <p className="text-sm text-gray-600">{app.user.email}</p>
                              )}
                              {app.location && (
                                <p className="text-sm text-gray-600 mt-1">{app.location}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                              Approved
                            </span>
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5 text-gray-600" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-600" />
                            )}
                          </div>
                        </button>
                        {isExpanded && (
                          <div className="px-6 pb-6">
                            {renderApplicationDetails(app)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Rejected Applications */}
            {rejectedApplications.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Rejected ({rejectedApplications.length})
                </h2>
                <div className="space-y-4">
                  {rejectedApplications.map((app) => {
                    const isExpanded = expandedAppId === app.id;
                    return (
                      <div key={app.id} className="bg-red-50 rounded-lg shadow-md border-2 border-red-200 overflow-hidden">
                        <button
                          onClick={() => setExpandedAppId(isExpanded ? null : app.id)}
                          className="w-full p-6 flex items-center justify-between hover:bg-red-100/50 transition-colors text-left"
                        >
                          <div className="flex items-center gap-3 flex-1">
                            {app.user?.photoURL ? (
                              <img
                                src={app.user.photoURL}
                                alt={app.user.displayName || 'User'}
                                className="w-12 h-12 rounded-full"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-600 to-green-700 flex items-center justify-center text-white font-medium">
                                {(app.user?.displayName || app.user?.email || 'U')[0].toUpperCase()}
                              </div>
                            )}
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-gray-900">
                                {app.user?.displayName || app.user?.email || 'Unknown User'}
                              </h3>
                              {app.user?.email && (
                                <p className="text-sm text-gray-600">{app.user.email}</p>
                              )}
                              {app.location && (
                                <p className="text-sm text-gray-600 mt-1">{app.location}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                              Rejected
                            </span>
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5 text-gray-600" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-600" />
                            )}
                          </div>
                        </button>
                        {isExpanded && (
                          <div className="px-6 pb-6">
                            {renderApplicationDetails(app)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
              </>
            ) : (
              <>
                {/* Pending Co-host Requests */}
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    Pending ({pendingCoHostRequests.length})
                  </h2>
                  {pendingCoHostRequests.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-md p-8 text-center">
                      <p className="text-gray-500">No pending co-host requests</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {pendingCoHostRequests.map((request) => {
                        const createdAt = request.createdAt?.toDate 
                          ? request.createdAt.toDate() 
                          : request.createdAt 
                          ? new Date(request.createdAt) 
                          : null;

                        return (
                          <div key={request.id} className="bg-white rounded-lg shadow-md p-6">
                            <div className="flex justify-between items-start mb-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-600 to-green-700 flex items-center justify-center text-white font-medium">
                                    {(request.name || request.email || 'U')[0].toUpperCase()}
                                  </div>
                                  <div>
                                    <h3 className="text-lg font-semibold text-gray-900">
                                      {request.name || 'Unknown'}
                                    </h3>
                                    {request.email && (
                                      <p className="text-sm text-gray-600">{request.email}</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleApproveCoHost(request.id)}
                                  disabled={approving === request.id}
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                  Approve
                                </button>
                                <button
                                  onClick={() => handleRejectCoHost(request.id)}
                                  disabled={approving === request.id}
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-700 hover:bg-red-800 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <XCircle className="w-4 h-4" />
                                  Reject
                                </button>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                              {request.location && (
                                <div className="flex items-start gap-2">
                                  <MapPin className="w-4 h-4 text-emerald-600 mt-1 flex-shrink-0" />
                                  <div>
                                    <p className="text-xs text-gray-500">Location</p>
                                    <p className="text-sm font-medium text-gray-900">{request.location}</p>
                                  </div>
                                </div>
                              )}
                              {request.email && (
                                <div className="flex items-start gap-2">
                                  <Mail className="w-4 h-4 text-emerald-600 mt-1 flex-shrink-0" />
                                  <div>
                                    <p className="text-xs text-gray-500">Email</p>
                                    <a
                                      href={`mailto:${request.email}`}
                                      className="text-sm font-medium text-emerald-700 hover:text-emerald-800"
                                    >
                                      {request.email}
                                    </a>
                                  </div>
                                </div>
                              )}
                              {request.website && (
                                <div className="flex items-start gap-2">
                                  <Globe className="w-4 h-4 text-emerald-600 mt-1 flex-shrink-0" />
                                  <div>
                                    <p className="text-xs text-gray-500">Website</p>
                                    <a
                                      href={request.website}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-sm font-medium text-emerald-700 hover:text-emerald-800"
                                    >
                                      {request.website}
                                    </a>
                                  </div>
                                </div>
                              )}
                              {request.instagram && (
                                <div className="flex items-start gap-2">
                                  <Instagram className="w-4 h-4 text-emerald-600 mt-1 flex-shrink-0" />
                                  <div>
                                    <p className="text-xs text-gray-500">Instagram</p>
                                    <p className="text-sm font-medium text-gray-900">{request.instagram}</p>
                                  </div>
                                </div>
                              )}
                              {request.socialMedia && (
                                <div className="flex items-start gap-2">
                                  <Globe className="w-4 h-4 text-emerald-600 mt-1 flex-shrink-0" />
                                  <div>
                                    <p className="text-xs text-gray-500">Social Media</p>
                                    <p className="text-sm font-medium text-gray-900">{request.socialMedia}</p>
                                  </div>
                                </div>
                              )}
                            </div>

                            {request.experience && (
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                <p className="text-xs text-gray-500 mb-1">Hosting Experience</p>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{request.experience}</p>
                              </div>
                            )}

                            {request.bio && (
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                <p className="text-xs text-gray-500 mb-1">About</p>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{request.bio}</p>
                              </div>
                            )}

                            {createdAt && (
                              <div className="mt-4 pt-4 border-t border-gray-200 flex items-center gap-2 text-xs text-gray-500">
                                <Clock className="w-3 h-3" />
                                <span>Requested {createdAt.toLocaleDateString('en-US', { 
                                  month: 'long', 
                                  day: 'numeric', 
                                  year: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit'
                                })}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Approved Co-host Requests */}
                {approvedCoHostRequests.length > 0 && (
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">
                      Approved ({approvedCoHostRequests.length})
                    </h2>
                    <div className="space-y-4">
                      {approvedCoHostRequests.map((request) => {
                        const isExpanded = expandedAppId === request.id;
                        return (
                          <div key={request.id} className="bg-green-50 rounded-lg shadow-md border-2 border-green-200 overflow-hidden">
                            <button
                              onClick={() => setExpandedAppId(isExpanded ? null : request.id)}
                              className="w-full p-6 flex items-center justify-between hover:bg-green-100/50 transition-colors text-left"
                            >
                              <div className="flex items-center gap-3 flex-1">
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-600 to-green-700 flex items-center justify-center text-white font-medium">
                                  {(request.name || request.email || 'U')[0].toUpperCase()}
                                </div>
                                <div className="flex-1">
                                  <h3 className="text-lg font-semibold text-gray-900">
                                    {request.name || 'Unknown'}
                                  </h3>
                                  {request.email && (
                                    <p className="text-sm text-gray-600">{request.email}</p>
                                  )}
                                  {request.location && (
                                    <p className="text-sm text-gray-600 mt-1">{request.location}</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                                  Approved
                                </span>
                                {isExpanded ? (
                                  <ChevronUp className="w-5 h-5 text-gray-600" />
                                ) : (
                                  <ChevronDown className="w-5 h-5 text-gray-600" />
                                )}
                              </div>
                            </button>
                            {isExpanded && (
                              <div className="px-6 pb-6">
                                {renderCoHostRequestDetails(request)}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Rejected Co-host Requests */}
                {rejectedCoHostRequests.length > 0 && (
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">
                      Rejected ({rejectedCoHostRequests.length})
                    </h2>
                    <div className="space-y-4">
                      {rejectedCoHostRequests.map((request) => {
                        const isExpanded = expandedAppId === request.id;
                        return (
                          <div key={request.id} className="bg-red-50 rounded-lg shadow-md border-2 border-red-200 overflow-hidden">
                            <button
                              onClick={() => setExpandedAppId(isExpanded ? null : request.id)}
                              className="w-full p-6 flex items-center justify-between hover:bg-red-100/50 transition-colors text-left"
                            >
                              <div className="flex items-center gap-3 flex-1">
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-600 to-green-700 flex items-center justify-center text-white font-medium">
                                  {(request.name || request.email || 'U')[0].toUpperCase()}
                                </div>
                                <div className="flex-1">
                                  <h3 className="text-lg font-semibold text-gray-900">
                                    {request.name || 'Unknown'}
                                  </h3>
                                  {request.email && (
                                    <p className="text-sm text-gray-600">{request.email}</p>
                                  )}
                                  {request.location && (
                                    <p className="text-sm text-gray-600 mt-1">{request.location}</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                                  Rejected
                                </span>
                                {isExpanded ? (
                                  <ChevronUp className="w-5 h-5 text-gray-600" />
                                ) : (
                                  <ChevronDown className="w-5 h-5 text-gray-600" />
                                )}
                              </div>
                            </button>
                            {isExpanded && (
                              <div className="px-6 pb-6">
                                {renderCoHostRequestDetails(request)}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Approvals;

